import { useEffect, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import BootstrapAlert from 'react-bootstrap/Alert';
import BootstrapSpinner from 'react-bootstrap/Spinner';

import * as K from 'common/constants';
import capitalize from 'lodash/capitalize';
import classNames from 'classnames';
import ELEMENT from 'common/element';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';

import './PageAlert.scss';

const ALERT_AUTO_HIDE = 5; // seconds
const DEFAULT_VARIANT = PageAlertVariant.INFO;

/**
 * A configurable alert component to display message/notification at the top center of the page.
 * You can toggle this  on or off from any component by setting the 'pageAlertConfig' in recoil state.
 *
 * Usage Examples:
 * const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
 *
 * // this shows an alert with message and a close button that will auto-hide
 * setPageAlertConfig({ message: 'Saved', autoHide: true });
 *
 * // this shows a Loading... alert with a spinner
 * setPageAlertConfig({ message: 'Loading...', inProgress: true });
 *
 * // this removes the alert (useful if not dismissible or auto-hide)
 * setPageAlertConfig(K.NO_PAGE_ALERT);
 *
 * Available configuration options:
 *
 * PageAlertConfig {
 *    id                      optional identifier
 *    message                 message to display
 *    heading                 heading to display
 *    dismissible             adds a close button (default)
 *    inProgress              displays a loading spinner
 *    autoHide                enables auto-hide
 *    duration                how long to show alert
 *    animateClass            a custom class for animation. Defaults to PageAlert--DropinAnimation.
 *                            Note: if it is to overwrite the dropin animation, must not use
 *                            the same name as PageAlertDropin.
 *    containerClassName      class for the root element of PageAlert
 *    variant                 color variant. Defaults to 'info'
 *    children                custom implementation of the PageAlert UI
 * }
 */
function PageAlert(): JSX.Element | null {
  const pageAlertConfig = useRecoilValue(pageAlertConfigState);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [show, setShow] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(); // auto-hide timeout stored in Ref so we can cancel timeout.

  /* Settings */
  const dismissible = pageAlertConfig.dismissible !== false && pageAlertConfig.children === undefined;

  useEffect(() => {
    const alertVisible = pageAlertConfig.message || pageAlertConfig.inProgress || pageAlertConfig.children;

    if (alertVisible) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setShow(true);
    } else {
      setShow(false);
    }
  }, [pageAlertConfig.message, pageAlertConfig.inProgress, pageAlertConfig.children]);

  /**
   * onClose() is called when the close button is clicked or when it is about to auto-hide.
   */
  const onClose = (): void => {
    setPageAlertConfig(K.NO_PAGE_ALERT);
    setShow(false);
  };

  if (!show) {
    return null;
  }

  if (pageAlertConfig.autoHide) {
    timerRef.current = setTimeout(() => {
      onClose();
    }, (pageAlertConfig.duration || ALERT_AUTO_HIDE) * 1000);
  }

  const spinner = pageAlertConfig.inProgress ? (
    <BootstrapSpinner animation="border" className="PageAlert__Spinner" />
  ) : null;
  const heading = pageAlertConfig.heading ? (
    <BootstrapAlert.Heading className="PageAlert__Heading">{pageAlertConfig.heading}</BootstrapAlert.Heading>
  ) : null;
  const variantClass = 'PageAlert--' + capitalize(pageAlertConfig.variant || DEFAULT_VARIANT);
  const animateClass = pageAlertConfig.animateClass || 'PageAlert--DropinAnimation';
  const alertClass = classNames('PageAlert', variantClass, animateClass, pageAlertConfig.containerClassName);
  const message =
    pageAlertConfig.linkHref && pageAlertConfig.linkText ? (
      <>
        {pageAlertConfig.message}
        <a className="PageAlert__Link" href={pageAlertConfig.linkHref}>
          {pageAlertConfig.linkText}
        </a>
      </>
    ) : (
      pageAlertConfig.message
    );

  const children = pageAlertConfig.children || (
    <>
      {heading}
      {spinner}
      <span className="mr-auto">{message}</span>
    </>
  );

  return (
    <BootstrapAlert
      className={alertClass}
      onClose={onClose}
      show={show}
      dismissible={dismissible}
      data-test-id={ELEMENT.commonPageAlert}
    >
      {children}
    </BootstrapAlert>
  );
}

export default PageAlert;
